"use client";

import { PushToDataLayer } from "@asosteam/asos-web-event-tracking-bridge";
import { useEffect, useRef, useCallback } from "react";
import { SavedItem } from "@repo/apis/customers-saveditems-v3";
import { useStore } from "@repo/ui/store";
import { mapWishlistItems } from "./analytics-helpers";
import { useAnalytics } from "./initialise-analytics";

const DEFAULT_CURRENCY = "GBP";

type PushViewItemWishlistEventOptions = {
  pushToDataLayer: PushToDataLayer;
  wishlistItems: SavedItem[];
  localCurrencyCode: string;
};

type PushAddToBagWishlistEventOptions = {
  pushToDataLayer: PushToDataLayer;
  wishlistItem: SavedItem;
  variantId: number;
  localCurrencyCode: string;
};

const pushViewItemWishlistEvent = ({
  pushToDataLayer,
  wishlistItems,
  localCurrencyCode,
}: PushViewItemWishlistEventOptions) => {
  const mappedItems = mapWishlistItems(wishlistItems);

  pushToDataLayer({
    view_item_list: {
      item_list_name: "wishlist",
      ecommerce: {
        currency: DEFAULT_CURRENCY,
        localCurrencyCode,
        items: mappedItems,
      },
    },
  });
};

const pushAddToBagWishlistEvent = ({
  pushToDataLayer,
  wishlistItem,
  variantId,
  localCurrencyCode,
}: PushAddToBagWishlistEventOptions) => {
  const priceLocal = wishlistItem.price?.current?.value;
  const mappedItem = mapWishlistItems([wishlistItem]);
  const variant = wishlistItem.variants.find(
    (variant) => variant.id === variantId,
  );

  if (!variant) {
    return;
  }

  const mergedItem = {
    ...mappedItem[0],
    size: variant.size,
    variantId: String(variant.id),
  };

  pushToDataLayer({
    add_to_cart: {
      ecommerce: {
        currency: DEFAULT_CURRENCY,
        localCurrencyCode,
        valueLocalCurrency: priceLocal,
        items: [mergedItem],
      },
    },
  });
};

export const useWishlistAnalytics = (
  wishlistItems: SavedItem[],
  savedItemsStatus: string,
) => {
  const { pushToDataLayer } = useAnalytics();
  const { countryData } = useStore();
  const hasSentViewEvent = useRef(false);
  const isReady = savedItemsStatus === "ready";
  const localCurrencyCode = countryData.defaultCurrency;

  const pushAddToBagEvent = useCallback(
    (wishlistItem: SavedItem, variantId?: number) => {
      if (!isReady) {
        return;
      }

      if (!variantId) {
        return;
      }

      pushAddToBagWishlistEvent({
        pushToDataLayer,
        wishlistItem,
        variantId,
        localCurrencyCode,
      });
    },
    [isReady, pushToDataLayer, localCurrencyCode],
  );

  useEffect(() => {
    if (!isReady || wishlistItems.length === 0 || hasSentViewEvent.current) {
      return;
    }

    pushViewItemWishlistEvent({
      pushToDataLayer,
      wishlistItems,
      localCurrencyCode,
    });

    hasSentViewEvent.current = true;
  }, [isReady, wishlistItems, pushToDataLayer, localCurrencyCode]);

  return {
    isReady,
    pushAddToBagEvent,
  };
};
